import React, { useState } from 'react';
import { FaPaperPlane, FaCheck, FaExclamationTriangle } from 'react-icons/fa';

const ContactForm = ({ formType = 'contact' }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    date: '',
    time: '',
    message: '',
    urgency: 'normal',
  });

  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const services = [
    'Select a Service',
    'Home Nursing',
    'Elderly Care',
    'Patient Attendants',
    'Physiotherapy',
    'Doctor Home Visit',
    'Lab Sample Collection',
    'Medical Equipment Rental',
    'Ambulance Support',
    'Other',
  ];

  const urgencyLevels = [
    { value: 'normal', label: 'Normal (24-48 hours)', color: 'bg-blue-100 text-blue-800' },
    { value: 'urgent', label: 'Urgent (Same day)', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'emergency', label: 'Emergency (Immediate)', color: 'bg-red-100 text-red-800' },
  ];

  const getFormTitle = () => {
    switch (formType) {
      case 'appointment':
        return 'Book an Appointment';
      case 'feedback':
        return 'Share Your Feedback';
      default:
        return 'Send us a Message';
    }
  };

  const getFormDescription = () => {
    switch (formType) {
      case 'appointment':
        return 'Schedule a home visit or consultation with our healthcare professionals';
      case 'feedback':
        return 'We value your feedback to improve our services';
      default:
        return 'Get in touch with our team for any inquiries or support';
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formType === 'appointment' && !formData.service) {
      newErrors.service = 'Please select a service';
    }

    if (formType === 'appointment' && !formData.date) {
      newErrors.date = 'Please select a preferred date';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    // Simulate API call
    setTimeout(() => {
      console.log('Form submitted:', formData);
      setLoading(false);
      setSubmitted(true);
      
      // Reset form after submission
      setTimeout(() => {
        setFormData({
          name: '',
          phone: '',
          email: '',
          service: '',
          date: '',
          time: '',
          message: '',
          urgency: 'normal',
        });
        setSubmitted(false);
      }, 3000);
    }, 1500);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <FaCheck className="text-3xl text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-4">Thank You!</h3>
        <p className="text-gray-600 mb-6">
          {formType === 'appointment'
            ? 'Your appointment request has been received. Our team will contact you shortly to confirm the details.'
            : 'Your message has been sent successfully. We will get back to you soon.'}
        </p>
        <p className="text-sm text-gray-500">
          Response time: Within 30 minutes (for urgent requests)
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2 font-poppins">
          {getFormTitle()}
        </h2>
        <p className="text-gray-600">{getFormDescription()}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name */}
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-health-blue focus:border-transparent`}
              placeholder="Enter your full name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <FaExclamationTriangle className="mr-1" /> {errors.name}
              </p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              Phone Number *
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                +91
              </div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full pl-14 pr-4 py-3 rounded-lg border ${
                  errors.phone ? 'border-red-300' : 'border-gray-300'
                } focus:outline-none focus:ring-2 focus:ring-health-blue focus:border-transparent`}
                placeholder="98765 43210"
              />
            </div>
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <FaExclamationTriangle className="mr-1" /> {errors.phone}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Email */}
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-health-blue focus:border-transparent`}
              placeholder="your@email.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <FaExclamationTriangle className="mr-1" /> {errors.email}
              </p>
            )}
          </div>

          {/* Service (for appointment) */}
          {formType === 'appointment' && (
            <div>
              <label className="block text-gray-700 mb-2 font-medium">
                Service Required *
              </label>
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.service ? 'border-red-300' : 'border-gray-300'
                } focus:outline-none focus:ring-2 focus:ring-health-blue focus:border-transparent`}
              >
                {services.map((service, index) => (
                  <option key={index} value={service}>
                    {service}
                  </option>
                ))}
              </select>
              {errors.service && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <FaExclamationTriangle className="mr-1" /> {errors.service}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Date and Time (for appointment) */}
        {formType === 'appointment' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 mb-2 font-medium">
                Preferred Date *
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.date ? 'border-red-300' : 'border-gray-300'
                } focus:outline-none focus:ring-2 focus:ring-health-blue focus:border-transparent`}
              />
              {errors.date && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <FaExclamationTriangle className="mr-1" /> {errors.date}
                </p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 mb-2 font-medium">
                Preferred Time
              </label>
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-health-blue focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Urgency Level */}
        <div>
          <label className="block text-gray-700 mb-3 font-medium">
            Urgency Level
          </label>
          <div className="flex flex-wrap gap-3">
            {urgencyLevels.map((level) => (
              <label
                key={level.value}
                className={`flex items-center px-4 py-2 rounded-full cursor-pointer transition-all ${
                  formData.urgency === level.value
                    ? `${level.color} ring-2 ring-offset-2 ring-opacity-50`
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <input
                  type="radio"
                  name="urgency"
                  value={level.value}
                  checked={formData.urgency === level.value}
                  onChange={handleChange}
                  className="sr-only"
                />
                <span className="text-sm font-medium">{level.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="block text-gray-700 mb-2 font-medium">
            Message *
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className={`w-full px-4 py-3 rounded-lg border ${
              errors.message ? 'border-red-300' : 'border-gray-300'
            } focus:outline-none focus:ring-2 focus:ring-health-blue focus:border-transparent`}
            placeholder={
              formType === 'appointment'
                ? 'Please provide details about the patient condition, symptoms, and any specific requirements...'
                : formType === 'feedback'
                ? 'Please share your experience, suggestions, or concerns...'
                : 'How can we help you today?'
            }
          />
          {errors.message && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <FaExclamationTriangle className="mr-1" /> {errors.message}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-health-blue text-white px-8 py-4 rounded-full font-bold hover:bg-blue-700 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-3 text-white" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Processing...
              </>
            ) : (
              <>
                <FaPaperPlane className="mr-2" />
                {formType === 'appointment'
                  ? 'Book Appointment Now'
                  : formType === 'feedback'
                  ? 'Submit Feedback'
                  : 'Send Message'}
              </>
            )}
          </button>
          
          <p className="text-center text-gray-500 text-sm mt-4">
            By submitting, you agree to our{' '}
            <a href="#" className="text-health-blue hover:underline">
              Privacy Policy
            </a>
            . We'll contact you within 30 minutes for urgent requests.
          </p>
        </div>
      </form>

      {/* Alternative Contact Methods */}
      <div className="mt-12 pt-8 border-t border-gray-200">
        <h4 className="text-lg font-bold text-gray-800 mb-4">Prefer to contact directly?</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="tel:+918266958207"
            className="flex items-center justify-center space-x-2 bg-blue-50 text-blue-700 px-6 py-3 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <span className="text-lg">📞</span>
            <span>Call Now</span>
          </a>
          <a
            href="https://wa.me/918266958207"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center space-x-2 bg-green-50 text-green-700 px-6 py-3 rounded-lg hover:bg-green-100 transition-colors"
          >
            <span className="text-lg">💬</span>
            <span>WhatsApp Chat</span>
          </a>
          <a
            href="mailto:leritohealthcare@gmail.com"
            className="flex items-center justify-center space-x-2 bg-red-50 text-red-700 px-6 py-3 rounded-lg hover:bg-red-100 transition-colors"
          >
            <span className="text-lg">✉️</span>
            <span>Send Email</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;